import { dailyXp } from './xp';

/**
 * A journey run climbs 4 → 5 → 6 → 7 on consecutive wins; finishing rung 7
 * completes the journey and starts a fresh run at 4. Any loss resets the
 * run to rung 4 (spec §10.3).
 *
 * Pure and storage-free: the web owns the keys and the random word pick,
 * so this module stays portable and deterministic.
 */
export interface JourneyState {
  /** The length being played now: 4, 5, 6 or 7. */
  rung: number;
  /** Wins in the current run. For a run in progress, always `rung - 4`. */
  runWins: number;
  /** Completed journeys, ever. */
  journeysCompleted: number;
  /** Highest rung ever reached. Monotone — a loss never lowers it. */
  bestRung: number;
}

export const FIRST_RUNG = 4;
export const FINAL_RUNG = 7;
export const JOURNEY_RUNGS: readonly number[] = [4, 5, 6, 7];

/** Flat bonus for finishing rung 7 (spec §10.3). Awarded once per journey. */
export const JOURNEY_COMPLETE_BONUS = 50;

export const EMPTY_JOURNEY: JourneyState = Object.freeze({
  rung: FIRST_RUNG,
  runWins: 0,
  journeysCompleted: 0,
  bestRung: FIRST_RUNG,
});

/**
 * True when finishing this game finishes the whole journey — i.e. a win on
 * the final rung. Takes the state *before* the outcome is applied, because
 * afterwards the rung has already reset to 4.
 */
export function journeyCompletedBy(state: JourneyState, won: boolean): boolean {
  return won && state.rung >= FINAL_RUNG;
}

/** Pure transition for a finished journey game. */
export function applyJourneyOutcome(state: JourneyState, won: boolean): JourneyState {
  if (!won) {
    // The run collapses; the record of how far it got does not.
    return { ...state, rung: FIRST_RUNG, runWins: 0 };
  }
  if (state.rung >= FINAL_RUNG) {
    return {
      rung: FIRST_RUNG,
      runWins: 0,
      journeysCompleted: state.journeysCompleted + 1,
      bestRung: Math.max(state.bestRung, FINAL_RUNG),
    };
  }
  const rung = state.rung + 1;
  return {
    rung,
    runWins: state.runWins + 1,
    journeysCompleted: state.journeysCompleted,
    bestRung: Math.max(state.bestRung, rung),
  };
}

/**
 * XP for a finished journey game: half the daily rate at that length,
 * rounded, with no streak bonus (spec §10.1/§10.3).
 *
 * Derived from `dailyXp` rather than re-tabulated, so the length
 * multiplier, the rounding point and the hint penalty can only ever be
 * defined once. Halving after the hint penalty means a hint costs 5 here
 * instead of 10 — the same proportion of a game worth half as much. The
 * journey-completion bonus is NOT part of this: it depends on the run, not
 * on the game, and is added by the caller that owns the journey state.
 */
export function journeyXp(
  won: boolean,
  guessCount: number,
  hintsUsed: number,
  length: number,
): number {
  return Math.round(dailyXp(won, guessCount, 0, hintsUsed, length) / 2);
}

/**
 * Structural guard for a stored journey state (spec §7: corrupt storage
 * resets to defaults, never crashes). The `rung === FIRST_RUNG + runWins`
 * invariant is checked too — a win moves both and a loss zeroes both, so
 * anything else was hand-edited or half-written.
 */
export function isJourneyState(v: unknown): v is JourneyState {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  const j = v as Record<string, unknown>;
  const numbers = [j.rung, j.runWins, j.journeysCompleted, j.bestRung];
  if (!numbers.every((n) => typeof n === 'number' && Number.isInteger(n) && n >= 0)) return false;
  const rung = j.rung as number;
  const bestRung = j.bestRung as number;
  if (!JOURNEY_RUNGS.includes(rung) || !JOURNEY_RUNGS.includes(bestRung)) return false;
  if (rung !== FIRST_RUNG + (j.runWins as number)) return false;
  return bestRung >= rung;
}
